import type { Point, DeliveryZone } from "@lib/zod/deliverySchema";
import { isInsideCircle } from "./isInsideCircle";
import { isInsideRectangle } from "./isInsideRectangle";
import { isInsidePolygon } from "./isInsidePolygon";
import { calculateDistance } from "./calculateDistance";
import type { TaxSettings } from "@lib/zod/taxSchema";
import { calculateFeeTax } from "./calculateFeeTax";
import { fixMath } from "./fixMath";

export function deliveryZoneFilterByLocation(userLocation: Point, deliveryZones: DeliveryZone[]) {
    return deliveryZones.filter(zone => zone.isActive).filter((zone) => {
        if (zone.shapeType === 'circle') {
            return isInsideCircle(userLocation, zone.circleArea!);
        } else if (zone.shapeType === 'rectangle') {
            return isInsideRectangle(userLocation, zone.rectangleArea!);
        } else if (zone.shapeType === 'polygon') {
            return isInsidePolygon(userLocation, zone.polygonArea!);
        }
        return false;
    });
}

export function getBestDeliveryZoneWithTaxDetails(
    deliveryLocation: Point,
    deliveryZones: DeliveryZone[],
    pickupLocation: Point,
    taxSettings: TaxSettings,
    uberDirectQuote?: number | undefined,
) {
    const bestZone = getBestDeliveryZone(
        deliveryLocation,
        deliveryZones,
        pickupLocation
    );

    if (!bestZone) {
        return null;
    }
    if (!taxSettings) {
        return null;
    }

    if (uberDirectQuote !== undefined) {
        // I assume uber direct quote is already including sales tax
        // because uber direct does not increase the final amount later
        const tax = calculateFeeTax(uberDirectQuote, 'ITEMS_PRICES_ALREADY_INCLUDE_TAXES', taxSettings.taxRateForDelivery ?? 0);
        const itemTotal = fixMath.subtract(uberDirectQuote, tax);
        const totalWithTax = fixMath.add(itemTotal, tax);
        const shownFee = taxSettings.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ? itemTotal : totalWithTax;
        return {
            itemTotal: (itemTotal),
            tax: (tax),
            totalWithTax: (totalWithTax),
            shownFee: (shownFee),
            // totalDeliveryFee: bestZone.totalDeliveryFee,
            // distanceFromStoreKm: bestZone.distanceFromStoreKm,
            customEstimatedDeliveryTime: bestZone.customEstimatedDeliveryTime,
            minCart: bestZone.minCart,
            // ...bestZone,
        };
    } else {
        const tax = calculateFeeTax(bestZone.totalDeliveryFee ?? 0, taxSettings.salesTaxType ?? 'ITEMS_PRICES_ALREADY_INCLUDE_TAXES', taxSettings.taxRateForDelivery ?? 0);
        const itemTotal = taxSettings.salesTaxType === 'ITEMS_PRICES_ALREADY_INCLUDE_TAXES' ? fixMath.subtract(bestZone.totalDeliveryFee ?? 0, tax) : (bestZone.totalDeliveryFee ?? 0);
        const totalWithTax = fixMath.add(itemTotal, tax);
        const shownFee = taxSettings.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ? itemTotal : totalWithTax;
        return {
            itemTotal: (itemTotal),
            tax: (tax),
            totalWithTax: (totalWithTax),
            shownFee: (shownFee),
            // totalDeliveryFee: bestZone.totalDeliveryFee,
            // distanceFromStoreKm: bestZone.distanceFromStoreKm,
            customEstimatedDeliveryTime: bestZone.customEstimatedDeliveryTime,
            minCart: bestZone.minCart,
            // ...bestZone,
        };
    }

}

export function getBestDeliveryZone(
    deliveryLocation: Point,
    deliveryZones: DeliveryZone[],
    pickupLocation: Point
) {
    const availableZones = deliveryZoneFilterByLocation(deliveryLocation, deliveryZones);
    if (availableZones.length === 0) {
        return null;
    }

    const distance = (calculateDistance(deliveryLocation, pickupLocation));

    // Calculate total delivery fees
    const zonesWithFees = availableZones.map((zone) => {
        const baseFee = zone.deliveryFee || 0;
        const distanceFee = zone.deliveryFeePerKm ? fixMath.multiply(zone.deliveryFeePerKm, distance) : 0;
        const totalDeliveryFee = fixMath.add(baseFee, distanceFee);

        return {
            ...zone,
            totalDeliveryFee: totalDeliveryFee, // raw fee as configured by store // without tax
            distanceFromStoreKm: distance
        };
    });

    // First sort by delivery fee, then by minimum cart as a tiebreaker
    zonesWithFees.sort((a, b) => {
        // First compare by delivery fee
        const feeDiff = fixMath.subtract(a.totalDeliveryFee!, b.totalDeliveryFee!);

        // If fees are the same (or very close), use minCart as tiebreaker
        if (Math.abs(feeDiff) < 0.3) {
            return fixMath.subtract(a.minCart || 0, b.minCart || 0);
        }

        return feeDiff;
    });

    return zonesWithFees[0] || null;
}